import React, { useEffect, useMemo, useState } from 'react';
import { SectionContainer } from './sectionContainer/SectionContainer';
import { TopBar } from './Header/Header';
import { windowResolutions } from '../utils/media';
import { sectionTypes } from './sectionContainer/sectionContainerTypes';
import { Footer } from './Footer/Footer';
import { SEOGlobalContext, SEOPageContext } from '../contexts/seoContext';
import { FacilityContext } from '../contexts/facilityContext';
import { LocationsContext } from '../contexts/locationsContext';
import { LayoutContext } from '../contexts/layoutContext';
import { VariantContext } from './homePageJumbotronDemo/hooks/Heightvariant';
import { GlobalContextProvider } from './Context/GlobalContext';
import { UnitsContextProvider } from 'contexts/unitsContext';
import { SessionTimerContextProvider } from './Context/SessionTimerContext';
import { Spinner } from '@codeparva/gatsby-theme-layout-components';
import {
  fetchFacilityAndGlobalEncKey,
  fetchMultipleFacilityEncKeys,
  fetchSmsConfigurations,
} from '@codeparva/v11-tfw-api-library';
import { getFacilityId } from './account/config';

const initialGlobalValues = {
  googleAnalyticsId: process.env.GA_ANALYTICS_ID || '',
  googleTagManagerId: process.env.GA_TAG_MANAGER_ID || '',
  metaTags: [],
  openGraphMetaTags: {},
  openGraphTwitter: {},
  canonicalUrl: '',
  jsonLdSchema: {},
  pageTitle: '',
};

const initialPageValues = {
  metaTags: [],
  openGraphMetaTags: {},
  openGraphTwitter: {},
  canonicalUrl: '',
  jsonLdSchema: {},
  pageTitle: '',
};

export default function Layout({ children, activePage }: sectionTypes.sectionContainerTypes) {
  const [headHeight, setheadHeight] = useState({ height: 50, screenWidth: 0 });
  const [globalSeo, setGlobalSeo] = useState(initialGlobalValues);
  const [pageSeo, setPageSeo] = useState(initialPageValues);
  const [facilityDetails, setFacilityDetails] = useState({});
  const [layoutDetails, setLayoutDetails] = useState({});
  const [allLocationDetails, setAllLocationDetails] = useState([]);
  const [hasMounted, setHasMounted] = useState(false);
  const [tfwConfig, setTfwConfig] = useState({});
  const [facilityAndGlobalEncKeys, setFacilityAndGlobalEncKeys] = useState<any>({
    globalEncKey: '',
    facilityEncPublicKeysPerFid: {},
  });
  const defaultFacilityId = getFacilityId('');

  const fetchTfwConfiguration = async (facPubKey?: string) => {
    const { facilityEncPublicKeysPerFid = {} } = facilityAndGlobalEncKeys;
    const [
      _,
      {
        data: {
          data: { Tenant_Websites: { data: [tenantWebsitesConfigInfo = {}] = [] } = {} } = {},
        } = {},
      } = {},
    ] = await fetchSmsConfigurations({
      configTypes: ['Tenant_Websites'],
      facilityId: defaultFacilityId,
      facPublicKey: facPubKey || facilityEncPublicKeysPerFid[defaultFacilityId],
    });
    const {
      value: {
        payment: { is_credit_card_payment_enabled = null, is_ach_payment_enabled = null } = {},
      } = {},
    } = tenantWebsitesConfigInfo;

    const isPaymentEnabled = is_credit_card_payment_enabled || is_ach_payment_enabled;
    setTfwConfig({ isPaymentEnabled, tenantWebsitesConfigInfo });
    setHasMounted(true);
  };

  useEffect(() => {
    fetchFacilityAndGlobalEncKey(defaultFacilityId).then(async ([_, response]) => {
      const {
        data: {
          data: {
            global_encryption_key: globalEncKey = '',
            facility_level_encryption_key: facPubKey = '',
          } = {},
        } = {},
      } = response;
      setFacilityAndGlobalEncKeys({
        globalEncKey,
        facilityEncPublicKeysPerFid: {
          [defaultFacilityId]: facPubKey,
        },
      });
      await fetchTfwConfiguration(facPubKey);
    });
  }, []);

  useEffect(() => {
    async function fetchAllLocationsFacilityEncKeys() {
      const allLocationsFacilityIds = allLocationDetails.map(
        ({ attributes: { facility_sms_id = '' } = {} }) => facility_sms_id,
      );
      if (allLocationsFacilityIds.length > 1) {
        const [
          _,
          {
            data: {
              data: { facility_level_encryption_keys: facilitiesEncKeysPerFid = {} } = {},
            } = {},
          } = {},
        ] = await fetchMultipleFacilityEncKeys(allLocationsFacilityIds);

        setFacilityAndGlobalEncKeys(({ facilityEncPublicKeysPerFid = {}, ...rest }) => ({
          ...rest,
          facilityEncPublicKeysPerFid: {
            ...facilityEncPublicKeysPerFid,
            ...facilitiesEncKeysPerFid,
          },
        }));
      }
    }

    if (Object.keys(allLocationDetails).length > 0) {
      fetchAllLocationsFacilityEncKeys();
    }
  }, [allLocationDetails]);

  const facilityContextValue = useMemo(() => {
    // NOTE: Please be careful if add a key here, you would need to check initializePage, because we set the facility data only if the facility details is empty.
    // Currently, we ignore isPaymentEnabled and tenantWebsitesConfigInfo while checking if facilityDetails is empty or not
    return {
      ...facilityDetails,
      ...tfwConfig,
      facilityAndGlobalEncKeys,
    };
  }, [facilityDetails, tfwConfig, facilityAndGlobalEncKeys]);

  const SectionContainerWrapper = (
    <VariantContext.Provider value={headHeight.height}>
      <TopBar onSetHeaderHeight={setheadHeight} />
      <SectionContainer
        data-testid="layout-section-container"
        style={{
          marginTop: headHeight.screenWidth >= windowResolutions.desktop ? headHeight.height : 0,
        }}
      >
        <SEOPageContext.Provider value={pageSeo}>
          {React.cloneElement(children, {
            setGlobalSeo,
            setPageSeo,
            setFacilityDetails,
            setAllLocationDetails,
            setLayoutDetails,
          })}
        </SEOPageContext.Provider>
      </SectionContainer>
      <Footer />
    </VariantContext.Provider>
  );

  const DynamicSectionContainer = (currentPage: string | undefined) => {
    switch (currentPage) {
      case 'view-rates':
        return <UnitsContextProvider>{SectionContainerWrapper}</UnitsContextProvider>;
      case 'account':
        return <SessionTimerContextProvider>{SectionContainerWrapper}</SessionTimerContextProvider>;
      default:
        return <>{SectionContainerWrapper}</>;
    }
  };

  const isActivePage = activePage ? activePage?.currentActivePage.description : 'everyOtherPage';

  return hasMounted ? (
    <div data-testid="website-layout">
      <FacilityContext.Provider value={facilityContextValue}>
        <LocationsContext.Provider value={allLocationDetails}>
          <LayoutContext.Provider value={layoutDetails}>
            <SEOGlobalContext.Provider value={globalSeo}>
              <GlobalContextProvider>{DynamicSectionContainer(isActivePage)}</GlobalContextProvider>
            </SEOGlobalContext.Provider>
          </LayoutContext.Provider>
        </LocationsContext.Provider>
      </FacilityContext.Provider>
    </div>
  ) : (
    <Spinner />
  );
}
